import React, { useState, useEffect } from 'react';
import { Row, Col, Button, notification, Layout, Typography, Spin } from 'antd';
import SEO from '../components/SEO';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircleOutlined, CrownOutlined, HeartOutlined, TeamOutlined, SafetyOutlined, StarOutlined, LoadingOutlined } from '@ant-design/icons';
import LandingNavbar from '../layouts/Navbar';
import CustomNavbar from '../components/Navbar';
import LandingFooter from '../layouts/Footer';
import PaymentService from '../services/paymentService';

const { Content } = Layout;
const { Title, Paragraph } = Typography;

const Pricing = () => {
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [, setNavbarReady] = useState(false);
    const [loading, setLoading] = useState(false);
    const [plans, setPlans] = useState([]);
    const [planConfiguration, setPlanConfiguration] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { selectedPlan } = location.state || {};

    const isAuthenticated = !!localStorage.getItem('user_id');

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch plans from API
    useEffect(() => {
        fetchPlans();
    }, []);

    // Show welcome message if user just logged in
    useEffect(() => {
        if (isAuthenticated && selectedPlan) {
            notification.success({
                message: 'Welcome Back!',
                description: `You can now purchase the ${selectedPlan.name} plan.`,
                duration: 4,
            });
        }
    }, [isAuthenticated, selectedPlan]);

    const fetchPlans = async () => {
        try {
            const configResponse = await PaymentService.getPlanConfiguration();

            if (configResponse.success) {
                setPlanConfiguration(configResponse.data);

                const { allPlans } = configResponse.data;

                const allPlansArray = [
                    allPlans.FREE      && { ...allPlans.FREE,      subscription_plan_id: null },
                    allPlans.BASIC     && { ...allPlans.BASIC,     subscription_plan_id: allPlans.BASIC.id },
                    allPlans.PREMIUM   && { ...allPlans.PREMIUM,   subscription_plan_id: allPlans.PREMIUM.id },
                    allPlans.ELITE     && { ...allPlans.ELITE,     subscription_plan_id: allPlans.ELITE.id },
                    allPlans.ULTIMATE  && { ...allPlans.ULTIMATE,  subscription_plan_id: allPlans.ULTIMATE.id },
                    allPlans.EXCLUSIVE && { ...allPlans.EXCLUSIVE, subscription_plan_id: allPlans.EXCLUSIVE.id },
                ].filter(Boolean);

                setPlans(allPlansArray);
            } else {
                notification.error({
                    message: 'Failed to load plans',
                    description: 'Please refresh the page to try again.',
                });
            }
        } catch (error) {
            notification.error({
                message: 'Error loading plans',
                description: 'Please check your connection and try again.',
            });
        }
    };

    const handlePlanSelect = async (plan) => {
        if (!isAuthenticated) {
            notification.info({
                message: 'Login Required',
                description: 'Please login to purchase a premium plan.',
            });
            navigate('/login', {
                state: {
                    redirectTo: location.pathname,
                    selectedPlan: plan
                }
            });
            return;
        }

        if (!plan.subscription_plan_id) {
            notification.error({
                message: 'Invalid Plan',
                description: 'This plan cannot be purchased.',
            });
            return;
        }

        setLoading(true);
        try {
            const response = await PaymentService.initiatePayment(plan.subscription_plan_id);

            if (response.success) {
                window.location.href = response.data.checkout_url;
            } else {
                notification.error({
                    message: 'Payment Initiation Failed',
                    description: response.message || 'Failed to initiate payment. Please try again.',
                });
            }
        } catch (error) {
            notification.error({
                message: 'Payment Error',
                description: 'Failed to initiate payment. Please try again.',
            });
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount, currency = 'INR') => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: currency,
        }).format(amount);
    };

    const isMobile = windowWidth < 768;

    // Override API colors to match the app theme
    const PLAN_COLORS = {
        free: '#64748B',
        basic: '#D81B60',
        premium: '#AD1457',
        ultimate: '#F59E0B',
    };
    const planColor = (plan) => PLAN_COLORS[plan.plan_id] || plan.color;

    const renderPlanCard = (plan) => {
        const color = planColor(plan);
        return (
        <div
            key={plan.id}
            style={{
                position: 'relative',
                borderRadius: '20px',
                overflow: 'hidden',
                background: 'white',
                border: plan.popular ? `2px solid ${color}` : '1px solid rgba(0,0,0,0.08)',
                boxShadow: plan.popular
                    ? `0 12px 40px ${color}30`
                    : '0 4px 20px rgba(0,0,0,0.07)',
                transform: 'none',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                transition: 'box-shadow 0.3s ease, transform 0.3s ease',
            }}
        >
            {/* Coloured header strip — fixed height so all cards align */}
            <div style={{
                background: `linear-gradient(135deg, ${color} 0%, ${color}bb 100%)`,
                padding: '28px 24px 20px',
                textAlign: 'center',
                position: 'relative',
                minHeight: '210px',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}>
                {plan.popular && (
                    <div style={{
                        position: 'absolute', top: '12px', right: '12px',
                        background: 'rgba(255,255,255,0.22)',
                        border: '1px solid rgba(255,255,255,0.4)',
                        borderRadius: '9999px',
                        padding: '3px 10px',
                        fontSize: '0.68rem', fontWeight: '700',
                        color: 'white', letterSpacing: '0.8px', textTransform: 'uppercase',
                    }}>
                        <StarOutlined style={{ marginRight: '4px', fontSize: '0.65rem' }} />POPULAR
                    </div>
                )}

                {/* Icon */}
                <div style={{
                    width: '56px', height: '56px', borderRadius: '16px',
                    background: 'rgba(255,255,255,0.2)',
                    border: '1px solid rgba(255,255,255,0.35)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: '14px',
                }}>
                    {plan.plan_id === 'free'
                        ? <HeartOutlined style={{ fontSize: '1.5rem', color: 'white' }} />
                        : <CrownOutlined style={{ fontSize: '1.5rem', color: 'white' }} />}
                </div>

                <div style={{ fontSize: '1.15rem', fontWeight: '800', color: 'white', marginBottom: '4px' }}>
                    {plan.name}
                </div>

                <div style={{ fontSize: '2.2rem', fontWeight: '800', color: 'white', lineHeight: 1.1 }}>
                    {plan.plan_id === 'free' ? '₹0' : formatCurrency(plan.price)}
                </div>
                <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.8)', marginTop: '4px', fontWeight: '500' }}>
                    {plan.plan_id === 'free' ? 'Forever free' : `for ${plan.duration}`}
                </div>

                {/* Always reserve the same height — show savings or empty space */}
                <div style={{ height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '10px' }}>
                    {plan.savings && (
                        <div style={{
                            background: 'rgba(255,255,255,0.22)',
                            border: '1px solid rgba(255,255,255,0.35)',
                            borderRadius: '9999px',
                            padding: '3px 12px',
                            fontSize: '0.78rem', fontWeight: '700', color: 'white',
                        }}>
                            {plan.savings}
                        </div>
                    )}
                </div>
            </div>

            {/* Features */}
            <div style={{ padding: '20px 24px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ flex: 1 }}>
                    {plan.features.map((feature, index) => (
                        <div key={index} style={{
                            display: 'flex', alignItems: 'flex-start', gap: '10px',
                            marginBottom: '10px',
                        }}>
                            <CheckCircleOutlined style={{
                                color, fontSize: '15px',
                                marginTop: '2px', flexShrink: 0,
                            }} />
                            <span style={{ fontSize: '0.9rem', color: '#374151', lineHeight: '1.5' }}>
                                {feature}
                            </span>
                        </div>
                    ))}
                </div>

                <Button
                    type="primary"
                    loading={loading && plan.plan_id !== 'free'}
                    onClick={() => plan.plan_id === 'free' ? navigate('/signup') : handlePlanSelect(plan)}
                    style={{
                        marginTop: '20px',
                        width: '100%',
                        height: '44px',
                        background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
                        borderColor: color,
                        borderRadius: '10px',
                        fontWeight: '700',
                        fontSize: '0.95rem',
                        boxShadow: `0 4px 16px ${color}30`,
                        transition: 'all 0.25s ease',
                    }}
                >
                    {plan.plan_id === 'free' ? 'Get Started for Free' : (isAuthenticated ? 'Choose Plan' : 'Get Started')}
                </Button>
            </div>
        </div>
        );
    };

    return (
        <Layout style={{ fontFamily: 'Calibri, sans-serif', background: '#FFFAFC', minHeight: '100vh' }}>
            <SEO
                title="Membership Plans & Pricing"
                description="Explore Oottan's affordable premium membership plans. Unlock unlimited contact views, priority listing, and more to find your life partner faster."
                canonical="/pricing"
            />
            {isAuthenticated ? (
                <CustomNavbar onNavbarReady={() => setNavbarReady(true)} />
            ) : (
                <LandingNavbar />
            )}

            <Content>
                {/* ── Hero Banner ── */}
                <div style={{
                    background: 'linear-gradient(135deg, #6A0572 0%, #880E6E 40%, #AD1457 80%, #D81B60 100%)',
                    padding: isMobile ? '80px 24px 48px' : '110px 24px 64px',
                    textAlign: 'center',
                    position: 'relative', overflow: 'hidden',
                }}>
                    <div style={{
                        position: 'absolute', inset: 0,
                        backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
                        backgroundSize: '28px 28px',
                    }} />
                    <div style={{ position: 'relative', zIndex: 1 }}>
                        <Title level={1} style={{
                            color: 'white', fontWeight: '800', marginBottom: '12px',
                            fontSize: isMobile ? '2rem' : '2.6rem', letterSpacing: '-0.5px',
                        }}>
                            Choose Your Perfect Plan
                        </Title>
                        <Paragraph style={{
                            color: 'rgba(255,255,255,0.78)',
                            fontSize: isMobile ? '1rem' : '1.15rem',
                            maxWidth: '560px', margin: '0 auto',
                        }}>
                            Start your journey to find your perfect match with our premium features
                        </Paragraph>
                    </div>
                </div>

                <div style={{ maxWidth: '1280px', margin: '0 auto', padding: isMobile ? '32px 16px' : '56px 32px' }}>

                    {/* ── Plan Cards ── */}
                    {plans.length > 0 ? (
                        <Row justify="center" gutter={[20, 20]} style={{ marginBottom: isMobile ? '32px' : '56px' }}>
                            {plans
                                .filter(plan => isAuthenticated ? plan.plan_id !== 'free' : true)
                                .map((plan) => (
                                    <Col key={plan.id} xs={24} sm={12} md={12} lg={6} style={{ display: 'flex' }}>
                                        {renderPlanCard(plan)}
                                    </Col>
                                ))
                            }
                        </Row>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '60px 0' }}>
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: '#AD1457' }} spin />} />
                            <div style={{ marginTop: '16px', color: '#64748B' }}>Loading plans…</div>
                        </div>
                    )}

                    {/* ── Subscription Benefits ── */}
                    {planConfiguration?.subscriptionBenefits?.length > 0 && (
                        <div style={{ marginBottom: isMobile ? '32px' : '56px' }}>
                            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                                <Title level={2} style={{
                                    fontSize: isMobile ? '1.6rem' : '2rem',
                                    fontWeight: '800', color: '#1A1A2E', marginBottom: '8px',
                                }}>
                                    Why Choose Premium?
                                </Title>
                                <div style={{ width: '48px', height: '4px', borderRadius: '9999px', background: 'linear-gradient(135deg,#AD1457,#D81B60)', margin: '0 auto' }} />
                            </div>
                            <Row gutter={[16, 16]}>
                                {planConfiguration.subscriptionBenefits.map((benefit, index) => (
                                    <Col key={index} xs={24} sm={12} md={6}>
                                        <div style={{
                                            background: 'white', borderRadius: '16px', padding: '28px 20px',
                                            textAlign: 'center', height: '100%',
                                            border: '1px solid rgba(173,20,87,0.08)',
                                            boxShadow: '0 4px 20px rgba(173,20,87,0.06)',
                                        }}>
                                            <div style={{ fontSize: '2.2rem', marginBottom: '14px' }}>{benefit.icon}</div>
                                            <Title level={4} style={{ fontSize: '1rem', fontWeight: '700', color: '#1A1A2E', marginBottom: '8px' }}>
                                                {benefit.title}
                                            </Title>
                                            <Paragraph style={{ color: '#64748B', fontSize: '0.9rem', marginBottom: 0 }}>
                                                {benefit.description}
                                            </Paragraph>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    )}

                    {/* ── Why Oottan banner ── */}
                    <div style={{
                        background: 'linear-gradient(135deg, #6A0572 0%, #880E6E 40%, #AD1457 80%, #D81B60 100%)',
                        borderRadius: '24px', padding: isMobile ? '36px 20px' : '56px 48px',
                        position: 'relative', overflow: 'hidden',
                        marginBottom: isMobile ? '32px' : '0',
                    }}>
                        <div style={{
                            position: 'absolute', inset: 0,
                            backgroundImage: 'radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)',
                            backgroundSize: '24px 24px',
                        }} />
                        <div style={{ position: 'relative', zIndex: 1 }}>
                            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                                <Title level={2} style={{
                                    fontSize: isMobile ? '1.6rem' : '2rem',
                                    fontWeight: '800', color: 'white', marginBottom: '8px',
                                }}>
                                    Why Choose Oottan?
                                </Title>
                                <div style={{ width: '48px', height: '4px', borderRadius: '9999px', background: 'rgba(255,255,255,0.4)', margin: '0 auto' }} />
                            </div>
                            <Row gutter={[16, 16]}>
                                {[
                                    { icon: <HeartOutlined />, title: 'Verified Profiles', desc: 'Every profile is verified for authenticity and trust' },
                                    { icon: <TeamOutlined />, title: 'Large Community', desc: 'Join thousands of active, verified members' },
                                    { icon: <SafetyOutlined />, title: 'Secure Platform', desc: 'Your privacy and data are our top priority' },
                                ].map((item, i) => (
                                    <Col key={i} xs={24} sm={12} md={8}>
                                        <div style={{
                                            background: 'rgba(255,255,255,0.1)',
                                            backdropFilter: 'blur(10px)',
                                            border: '1px solid rgba(255,255,255,0.18)',
                                            borderRadius: '16px', padding: '28px 20px', textAlign: 'center',
                                        }}>
                                            <div style={{
                                                width: '52px', height: '52px', borderRadius: '14px',
                                                background: 'rgba(255,255,255,0.18)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                margin: '0 auto 16px',
                                            }}>
                                                {React.cloneElement(item.icon, { style: { color: 'white', fontSize: '1.4rem' } })}
                                            </div>
                                            <Title level={4} style={{ color: 'white', fontWeight: '700', marginBottom: '8px', fontSize: '1.05rem' }}>
                                                {item.title}
                                            </Title>
                                            <Paragraph style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem', marginBottom: 0 }}>
                                                {item.desc}
                                            </Paragraph>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    </div>

                </div>
            </Content>

            <LandingFooter />
        </Layout>
    );
};

export default Pricing;